import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';

const { FiCopy, FiCheck, FiCode, FiExternalLink, FiDownload } = FiIcons;

function App() {
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('Embedded Content');
  const [copied, setCopied] = useState(false);
  const [generatedCode, setGeneratedCode] = useState('');

  const generateIframeCode = () => {
    if (!url) return;

    const code = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${title}</title>
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
       
        html, body {
            height: 100%;
            width: 100%;
            overflow: hidden;
        }
       
        #fullscreen-iframe {
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            border: none;
            z-index: 9999;
            background: white;
        }
       
        .loading-overlay {
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            background: linear-gradient(135deg, #000000 0%, #000000 100%);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 10000;
            transition: opacity 0.5s ease;
        }
       
        .loading-spinner {
            width: 50px;
            height: 50px;
            border: 3px solid rgba(255,255,255,0.3);
            border-radius: 50%;
            border-top-color: white;
            animation: spin 1s ease-in-out infinite;
        }
       
        @keyframes spin {
            to { transform: rotate(360deg); }
        }
    </style>
</head>
<body>
    <!-- Loading overlay -->
    <div class="loading-overlay" id="loadingOverlay">
        <div class="loading-spinner"></div>
    </div>

    <!-- Full-screen iframe -->
    <iframe
        id="fullscreen-iframe"
        src="${url}"
        title="${title}"
        allowfullscreen>
    </iframe>

    <script>
        // Hide loading overlay once iframe loads
        document.getElementById('fullscreen-iframe').onload = function() {
            setTimeout(() => {
                document.getElementById('loadingOverlay').style.opacity = '0';
                setTimeout(() => {
                    document.getElementById('loadingOverlay').style.display = 'none';
                }, 500);
            }, 500);
        };

        // Prevent parent page scrolling
        document.body.style.overflow = 'hidden';
       
        // Handle mobile viewport issues
        function setViewportHeight() {
            let vh = window.innerHeight * 0.01;
            document.documentElement.style.setProperty('--vh', \`\${vh}px\`);
        }
       
        setViewportHeight();
        window.addEventListener('resize', setViewportHeight);
    </script>
</body>
</html>`;

    setGeneratedCode(code);
  };

  const copyToClipboard = async () => {
    try {
      // Method 1: Try the modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(generatedCode);
      } else {
        // Method 2: Fallback for older browsers or non-secure contexts
        const textArea = document.createElement('textarea');
        textArea.value = generatedCode;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        textArea.remove();
      }
      
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
      // Method 3: Last resort fallback
      try {
        const textArea = document.createElement('textarea');
        textArea.value = generatedCode;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (fallbackErr) {
        console.error('Fallback copy also failed: ', fallbackErr);
        alert('Unable to copy code. Please select and copy manually.');
      }
    }
  };

  const downloadFile = () => {
    const blob = new Blob([generatedCode], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'iframe-fullscreen.html';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8 pt-8"
        >
          <div className="flex items-center justify-center mb-4">
            <SafeIcon icon={FiCode} className="text-4xl text-blue-600 mr-3" />
            <h1 className="text-4xl font-bold text-gray-900">
              HTML Iframe Generator
            </h1>
          </div>
          <p className="text-lg text-gray-600">
            Create full-screen iframe HTML code with loading animations
          </p>
        </motion.div>

        {/* Input Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl shadow-xl p-8 mb-8 border border-gray-200"
        >
          <div className="space-y-6">
            <div>
              <label className="block text-gray-900 font-semibold mb-3">
                <SafeIcon icon={FiExternalLink} className="inline mr-2" />
                Website URL
              </label>
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com"
                className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-300 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
              {url && !isValidUrl(url) && (
                <p className="text-red-500 text-sm mt-2">Please enter a valid URL</p>
              )}
            </div>

            <div>
              <label className="block text-gray-900 font-semibold mb-3">
                Page Title (Optional)
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Embedded Content"
                className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-300 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
            </div>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={generateIframeCode}
              disabled={!url || !isValidUrl(url)}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold py-4 px-6 rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              <SafeIcon icon={FiCode} className="mr-2" />
              Generate HTML Code
            </motion.button>
          </div>
        </motion.div>

        {/* Generated Code Output */}
        {generatedCode && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                <SafeIcon icon={FiCode} className="mr-2 text-blue-600" />
                Generated HTML Code
              </h3>
              <div className="flex space-x-3">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={copyToClipboard}
                  className="flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                >
                  <SafeIcon 
                    icon={copied ? FiCheck : FiCopy} 
                    className="mr-2" 
                  />
                  {copied ? 'Copied!' : 'Copy Code'}
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={downloadFile}
                  className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiDownload} className="mr-2" />
                  Download
                </motion.button>
              </div>
            </div>

            <div className="relative">
              <pre className="bg-gray-900 text-green-400 p-6 rounded-xl overflow-x-auto text-sm font-mono border border-gray-700 max-h-96 overflow-y-auto">
                <code>{generatedCode}</code>
              </pre>
            </div>

            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <h4 className="text-gray-900 font-semibold mb-2">How to use:</h4>
              <ul className="text-gray-600 text-sm space-y-1">
                <li>• Copy the generated HTML code above</li>
                <li>• Save it as an .html file (e.g., iframe.html)</li>
                <li>• Open the file in any web browser</li>
                <li>• The website will load in full-screen mode with a loading animation</li>
              </ul>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}

export default App;